import React, { useCallback } from 'react'
import {
    Box,
    Button,
    CircularProgress,
    Divider,
    Grid,
    Typography,
} from '@mui/material'
import WarningIcon from '@mui/icons-material/Warning'
import PrintIcon from '@mui/icons-material/Print'
import GetAppIcon from '@mui/icons-material/GetApp'
import VideocamIcon from '@mui/icons-material/Videocam'
import { ResponsivePageContainer } from '../../components/PageContainers'
import {
    useFmoStatements,
    useFmoStatementDetail,
    downloadFmoPdf,
    printFmoPdf,
} from '../../hooks/useMoveOutManagement/useMoveOutManagement'
import { useStyles } from './styles'
import { getStatusChip } from './statusHelpers'
import { InventoryIssueCard } from './InventoryIssueCard'
import { DamageCard } from './DamageCard'
import { UnpaidChargeCard } from './UnpaidChargeCard'

const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
    }).format(value)
}

const formatDate = (isoDate: string) => {
    const date = new Date(isoDate)
    return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
    })
}

export const MoveOut: React.FC = () => {
    const sx = useStyles()

    const {
        statements,
        isLoading: listLoading,
        error: listError,
    } = useFmoStatements()

    const activeStatementId = (statements ?? [])[0]?.id ?? null

    const {
        moveOutDetail: detail,
        statementType,
        pdfUrl,
        sentDate,
        isLoading: detailLoading,
        error: detailError,
    } = useFmoStatementDetail(activeStatementId)

    const isFmo = statementType === 'fmo'
    const isLoading = listLoading || detailLoading
    const error = listError || detailError

    const deposit = parseFloat(detail?.deposit ?? '0')
    const totalDamages = parseFloat(detail?.total_damages ?? '0')
    const pastDueBalance = parseFloat(detail?.past_due_balance ?? '0')
    const unpaidChargesTotal = (detail?.unpaid_charges ?? []).reduce(
        (sum, charge) => sum + parseFloat(charge.amount || '0'),
        0,
    )
    const totalDeductions = totalDamages + pastDueBalance
    const netAmount = deposit - totalDeductions

    const moveInInspections = detail?.tenant_move_in_inspection ?? []
    const moveInInventory = moveInInspections.flatMap((insp) =>
        (insp.inventory_inspections ?? [])
            .filter((inv) => (inv.issues ?? []).length > 0)
            .map((inv) => ({ ...inv, area_name: insp.area_name })),
    )
    const moveInCompletedDate = moveInInspections
        .map((insp) => insp.completed_date)
        .find((d) => d != null)

    const moveOutInspections = detail?.move_out_inspection ?? []
    const moveOutCompletedDate = moveOutInspections
        .map((insp) => insp.completed_date)
        .find((d) => d != null)
    const moveOutCompletedBy = moveOutInspections
        .map((insp) => insp.completed_by)
        .find((cb) => cb != null)
    const allVideos = moveOutInspections
        .flatMap((insp) =>
            (insp.videos ?? []).map((v) => ({ ...v, area_name: insp.area_name })),
        )
        .filter((v, i, arr) => arr.findIndex((x) => x.id === v.id) === i)

    const allDamages = detail?.damages ?? []

    const handleExportPdf = useCallback(() => {
        if (!pdfUrl || !detail) return
        const tenantName = detail.tenant.name.replace(/\s+/g, '-')
        const prefix = isFmo ? 'FMO' : 'FIR'
        const filename = `${prefix}-${detail.lease.unit_name}-${tenantName}.pdf`
        downloadFmoPdf(pdfUrl, filename)
    }, [pdfUrl, detail, isFmo])

    const handlePrint = useCallback(() => {
        if (!pdfUrl) return
        printFmoPdf(pdfUrl)
    }, [pdfUrl])

    const pageTitle = isFmo
        ? 'Final Move-Out Statement'
        : 'Final Inspection Report'

    return (
        <ResponsivePageContainer>
            <Box sx={sx.pageContainer}>
                {/* Header */}
                <Box sx={sx.pageHeader}>
                    <Typography
                        variant="h5"
                        sx={{ fontWeight: 700, color: '#fff' }}
                    >
                        {pageTitle}
                    </Typography>
                    {detail?.lease.final_move_out_status && (
                        <Box sx={{ mt: 1 }}>
                            {getStatusChip(
                                detail.lease.final_move_out_status,
                                isFmo,
                            )}
                        </Box>
                    )}
                    {sentDate && (
                        <Typography
                            variant="caption"
                            sx={{ color: '#94a3b8', mt: 0.5 }}
                        >
                            Sent {formatDate(sentDate)}
                        </Typography>
                    )}
                </Box>

                {/* Loading */}
                {isLoading && (
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            py: 6,
                        }}
                    >
                        <CircularProgress />
                    </Box>
                )}

                {/* Error */}
                {error && !isLoading && (
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            py: 6,
                            gap: 1,
                        }}
                    >
                        <WarningIcon sx={{ color: '#ef4444' }} />
                        <Typography
                            variant="body2"
                            sx={{ color: '#ef4444' }}
                        >
                            Failed to load details. Please try again.
                        </Typography>
                    </Box>
                )}

                {/* No statements */}
                {!isLoading &&
                    !error &&
                    (statements ?? []).length === 0 && (
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'center',
                                py: 6,
                            }}
                        >
                            <Typography
                                variant="body2"
                                sx={{ color: '#94a3b8' }}
                            >
                                No statements available yet.
                            </Typography>
                        </Box>
                    )}

                {/* Detail Content */}
                {detail && !isLoading && !error && (
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 3,
                        }}
                    >
                        {/* Lease & Tenant Info */}
                        <Grid container spacing={3}>
                            <Grid item xs={12} sm={6}>
                                <Typography sx={sx.sectionTitle}>
                                    Property Information
                                </Typography>
                                <Box sx={sx.infoCard}>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            fontWeight: 600,
                                            color: '#fff',
                                        }}
                                    >
                                        {detail.lease.apartment_name}
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        sx={{ color: '#94a3b8' }}
                                    >
                                        {detail.lease.apartment_address},{' '}
                                        {detail.lease.apartment_city},{' '}
                                        {detail.lease.apartment_state}{' '}
                                        {detail.lease.apartment_zip}
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            fontFamily: 'monospace',
                                            color: '#cbd5e1',
                                        }}
                                    >
                                        Unit {detail.lease.unit_name}
                                    </Typography>
                                </Box>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Typography sx={sx.sectionTitle}>
                                    Tenant Information
                                </Typography>
                                <Box sx={sx.infoCard}>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            fontWeight: 600,
                                            color: '#fff',
                                        }}
                                    >
                                        {detail.tenant.name}
                                    </Typography>
                                    {detail.tenant.email && (
                                        <Typography
                                            variant="body2"
                                            sx={{ color: '#94a3b8' }}
                                        >
                                            {detail.tenant.email}
                                        </Typography>
                                    )}
                                    {detail.tenant.phone && (
                                        <Typography
                                            variant="body2"
                                            sx={{ color: '#94a3b8' }}
                                        >
                                            {detail.tenant.phone}
                                        </Typography>
                                    )}
                                </Box>
                            </Grid>
                        </Grid>

                        {/* Dates Row */}
                        <Grid container spacing={2}>
                            <Grid item xs={4}>
                                <Box sx={sx.dateCard}>
                                    <Typography
                                        variant="caption"
                                        sx={{ color: '#94a3b8' }}
                                    >
                                        Lease Start
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            fontWeight: 600,
                                            color: '#fff',
                                        }}
                                    >
                                        {formatDate(detail.lease.start_date)}
                                    </Typography>
                                </Box>
                            </Grid>
                            <Grid item xs={4}>
                                <Box sx={sx.dateCard}>
                                    <Typography
                                        variant="caption"
                                        sx={{ color: '#94a3b8' }}
                                    >
                                        Move-Out
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            fontWeight: 600,
                                            color: '#fff',
                                        }}
                                    >
                                        {formatDate(detail.lease.end_date)}
                                    </Typography>
                                </Box>
                            </Grid>
                            <Grid item xs={4}>
                                <Box sx={sx.dateCard}>
                                    <Typography
                                        variant="caption"
                                        sx={{ color: '#94a3b8' }}
                                    >
                                        Inspection Complete
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            fontWeight: 600,
                                            color: '#fff',
                                        }}
                                    >
                                        {moveOutCompletedDate
                                            ? formatDate(moveOutCompletedDate)
                                            : 'Not completed'}
                                    </Typography>
                                </Box>
                            </Grid>
                        </Grid>

                        {/* Move In Inspection */}
                        <Box>
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    mb: 1.5,
                                }}
                            >
                                <Typography
                                    sx={
                                        {
                                            ...sx.sectionTitle,
                                            mb: 0,
                                        } as object
                                    }
                                >
                                    Move In Inspection
                                </Typography>
                                <Typography
                                    variant="caption"
                                    sx={{ color: '#94a3b8' }}
                                >
                                    {moveInCompletedDate
                                        ? `Completed ${formatDate(moveInCompletedDate)}`
                                        : 'Not completed'}
                                </Typography>
                            </Box>

                            {moveInInventory.length > 0 ? (
                                <Box
                                    sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: 1.5,
                                    }}
                                >
                                    {moveInInventory.map((inv) => (
                                        <InventoryIssueCard
                                            key={inv.id}
                                            inventoryInspection={inv}
                                            areaName={inv.area_name}
                                        />
                                    ))}
                                </Box>
                            ) : (
                                <Box sx={sx.infoCard}>
                                    <Typography
                                        variant="body2"
                                        sx={{ color: '#94a3b8' }}
                                    >
                                        No issues found in the move-in
                                        inspection.
                                    </Typography>
                                </Box>
                            )}
                        </Box>

                        {/* Move Out Inspection */}
                        <Box>
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    mb: 1.5,
                                }}
                            >
                                <Typography
                                    sx={
                                        {
                                            ...sx.sectionTitle,
                                            mb: 0,
                                        } as object
                                    }
                                >
                                    Move Out Inspection
                                </Typography>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1.5,
                                    }}
                                >
                                    {moveOutCompletedBy && (
                                        <Typography
                                            variant="caption"
                                            sx={{ color: '#94a3b8' }}
                                        >
                                            By {moveOutCompletedBy}
                                        </Typography>
                                    )}
                                    <Typography
                                        variant="caption"
                                        sx={{ color: '#94a3b8' }}
                                    >
                                        {moveOutCompletedDate
                                            ? `Completed ${formatDate(moveOutCompletedDate)}`
                                            : 'Not completed'}
                                    </Typography>
                                </Box>
                            </Box>

                            {/* Move-Out Videos */}
                            {allVideos.length > 0 && (
                                <Box
                                    sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: 2,
                                        mb: 2,
                                    }}
                                >
                                    {allVideos.map((video) => (
                                        <Box
                                            key={video.id}
                                            sx={
                                                {
                                                    ...sx.videoSection,
                                                } as object
                                            }
                                        >
                                            <Box sx={sx.videoHeader}>
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 1,
                                                    }}
                                                >
                                                    <VideocamIcon
                                                        sx={{
                                                            fontSize: 18,
                                                            color: '#fff',
                                                        }}
                                                    />
                                                    <Typography
                                                        variant="body2"
                                                        sx={{
                                                            fontWeight: 600,
                                                            color: '#fff',
                                                        }}
                                                    >
                                                        Inspection Video — {video.area_name}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                            <Box
                                                sx={{ backgroundColor: '#000' }}
                                            >
                                                <video
                                                    controls
                                                    style={{
                                                        width: '100%',
                                                        maxHeight: 400,
                                                        display: 'block',
                                                    }}
                                                >
                                                    <source src={video.url} />
                                                </video>
                                            </Box>
                                        </Box>
                                    ))}
                                </Box>
                            )}

                            {/* Damages */}
                            {allDamages.length > 0 ? (
                                <Box sx={sx.damageSection}>
                                    <Box sx={sx.damageHeader}>
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1,
                                            }}
                                        >
                                            <WarningIcon
                                                sx={{
                                                    fontSize: 18,
                                                    color: '#ef4444',
                                                }}
                                            />
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    fontWeight: 600,
                                                    color: '#ef4444',
                                                }}
                                            >
                                                Damages ({allDamages.length})
                                            </Typography>
                                        </Box>
                                    </Box>
                                    {allDamages.map((damage) => (
                                        <DamageCard
                                            key={damage.id}
                                            damage={damage}
                                        />
                                    ))}
                                    <Box sx={sx.damageTotal}>
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                fontWeight: 600,
                                                color: '#fff',
                                            }}
                                        >
                                            Total Damages
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                fontWeight: 700,
                                                color: '#ef4444',
                                            }}
                                        >
                                            {formatCurrency(totalDamages)}
                                        </Typography>
                                    </Box>
                                </Box>
                            ) : (
                                <Box sx={sx.infoCard}>
                                    <Typography
                                        variant="body2"
                                        sx={{ color: '#94a3b8' }}
                                    >
                                        No damages recorded.
                                    </Typography>
                                </Box>
                            )}
                        </Box>

                        {/* ===== FMO-only sections ===== */}
                        {isFmo && (
                            <>
                                {/* Past Due Balance / Unpaid Charges */}
                                <Box sx={sx.damageSection}>
                                    <Box
                                        sx={
                                            {
                                                ...sx.damageHeader,
                                                backgroundColor:
                                                    'rgba(245, 158, 11, 0.08)',
                                            } as object
                                        }
                                    >
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1,
                                            }}
                                        >
                                            <WarningIcon
                                                sx={{
                                                    fontSize: 18,
                                                    color: '#f59e0b',
                                                }}
                                            />
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    fontWeight: 600,
                                                    color: '#fbbf24',
                                                }}
                                            >
                                                Past Due Balance (
                                                {(detail.unpaid_charges ?? []).length})
                                            </Typography>
                                        </Box>
                                    </Box>
                                    {(detail.unpaid_charges ?? []).map((charge, idx) => (
                                        <UnpaidChargeCard
                                            key={idx}
                                            charge={charge}
                                        />
                                    ))}
                                    <Box sx={sx.damageTotal}>
                                        <Typography
                                            variant="body2"
                                            sx={{ fontWeight: 600, color: '#fff' }}
                                        >
                                            Total Past Due
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                fontWeight: 700,
                                                color: '#ef4444',
                                            }}
                                        >
                                            {formatCurrency(unpaidChargesTotal)}
                                        </Typography>
                                    </Box>
                                </Box>

                                <Divider
                                    sx={{ borderColor: 'rgba(255,255,255,0.1)' }}
                                />

                                {/* Security Deposit Disposition */}
                                <Box sx={sx.dispositionSection}>
                                    <Box sx={sx.dispositionHeader}>
                                        <Typography
                                            variant="body2"
                                            sx={{ fontWeight: 600, color: '#fff' }}
                                        >
                                            Security Deposit Disposition
                                        </Typography>
                                    </Box>
                                    <Box sx={sx.dispositionBody}>
                                        <Box sx={sx.dispositionRow}>
                                            <Typography
                                                variant="body2"
                                                sx={{ color: '#fff' }}
                                            >
                                                Security Deposit Held
                                            </Typography>
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    fontWeight: 600,
                                                    color: '#fff',
                                                }}
                                            >
                                                {formatCurrency(deposit)}
                                            </Typography>
                                        </Box>

                                        <Box sx={{ mt: 2, mb: 1 }}>
                                            <Typography
                                                variant="caption"
                                                sx={{
                                                    color: '#94a3b8',
                                                    textTransform: 'uppercase',
                                                    letterSpacing: '0.05em',
                                                    fontWeight: 600,
                                                }}
                                            >
                                                Deductions
                                            </Typography>
                                        </Box>
                                        <Box sx={sx.dispositionRow}>
                                            <Typography
                                                variant="body2"
                                                sx={{ color: '#94a3b8' }}
                                            >
                                                Damages
                                            </Typography>
                                            <Typography
                                                variant="body2"
                                                sx={{ color: '#ef4444' }}
                                            >
                                                - {formatCurrency(totalDamages)}
                                            </Typography>
                                        </Box>
                                        <Box sx={sx.dispositionRow}>
                                            <Typography
                                                variant="body2"
                                                sx={{ color: '#94a3b8' }}
                                            >
                                                Past Due Balance
                                            </Typography>
                                            <Typography
                                                variant="body2"
                                                sx={{ color: '#ef4444' }}
                                            >
                                                - {formatCurrency(pastDueBalance)}
                                            </Typography>
                                        </Box>
                                        <Box sx={sx.dispositionSubtotal}>
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    fontWeight: 600,
                                                    color: '#fff',
                                                }}
                                            >
                                                Total Deductions
                                            </Typography>
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    fontWeight: 600,
                                                    color: '#ef4444',
                                                }}
                                            >
                                                - {formatCurrency(totalDeductions)}
                                            </Typography>
                                        </Box>
                                    </Box>

                                    {/* Net Amount */}
                                    <Box
                                        sx={
                                            {
                                                ...sx.finalAmountBox,
                                                backgroundColor:
                                                    netAmount >= 0
                                                        ? 'rgba(16, 185, 129, 0.08)'
                                                        : 'rgba(239, 68, 68, 0.08)',
                                            } as object
                                        }
                                    >
                                        <Box>
                                            <Typography
                                                variant="subtitle1"
                                                sx={{
                                                    fontWeight: 600,
                                                    color: '#fff',
                                                }}
                                            >
                                                {netAmount >= 0
                                                    ? 'Refund Due to Tenant'
                                                    : 'Amount Owed by Tenant'}
                                            </Typography>
                                            <Typography
                                                variant="caption"
                                                sx={{ color: '#94a3b8' }}
                                            >
                                                {netAmount >= 0
                                                    ? 'To be returned within 21 days of move-out'
                                                    : 'Payment due within 30 days'}
                                            </Typography>
                                        </Box>
                                        <Typography
                                            variant="h4"
                                            sx={{
                                                fontWeight: 700,
                                                color:
                                                    netAmount >= 0
                                                        ? '#10b981'
                                                        : '#ef4444',
                                            }}
                                        >
                                            {formatCurrency(Math.abs(netAmount))}
                                        </Typography>
                                    </Box>
                                </Box>
                            </>
                        )}

                        {/* Action Buttons */}
                        <Box sx={sx.actionBar}>
                            <Button
                                variant="outlined"
                                startIcon={<PrintIcon />}
                                onClick={handlePrint}
                                disabled={!pdfUrl}
                                sx={{
                                    flex: 1,
                                    color: '#94a3b8',
                                    borderColor: 'rgba(255,255,255,0.2)',
                                }}
                            >
                                Print
                            </Button>
                            <Button
                                variant="outlined"
                                startIcon={<GetAppIcon />}
                                onClick={handleExportPdf}
                                disabled={!pdfUrl}
                                sx={{
                                    flex: 1,
                                    color: '#94a3b8',
                                    borderColor: 'rgba(255,255,255,0.2)',
                                }}
                            >
                                Download PDF
                            </Button>
                        </Box>
                    </Box>
                )}
            </Box>
        </ResponsivePageContainer>
    )
}
